import React, { ReactElement, useCallback, useMemo } from 'react'
import Tooltip from './Tooltip'
import TimeText from './TimeText'
import Table from './Table'
import { ExternalLinkText } from '../views/Events'
import { formatExplorerUrl } from '../util/Chains'
import { ManagedPaymentMethod } from '../util/Managed'
import { shortAddress, trimZeros } from '../util/Util'
import { RADOM_COLORS } from '../util/Constants'
import LinkExternal from './LinkExternal'
import { HoverInfoIcon } from '../icons/Info'
import { Section, SectionBreaker, SectionContent, SectionItem, SubTitle } from './Shared'
import {
  conversionTitleStyle,
  externalLinkTextStyle,
  icon18Style,
  rowCenterGap3Style,
  rowCenterGap5Font14Style,
  rowCenterGap5Style,
  titleStyle
} from './WithdrawalDetailsStyles'

interface WithdrawalDetailsProps {
  paymentMethod: ManagedPaymentMethod
  requestedAt: string
  network: string
  amount: string
  logo?: string
  isFiatWithdrawal?: boolean
  fiat?: {
    transferMethod: string
    currencyOut: 'EUR' | 'USD'
    fee: number
    receiptAccountId?: string
    finalAmount?: string
  }
  networkFeeAmount?: string
  finalAmountCrypto?: string
  completedAt?: string
  transactionHash?: string
}

const WithdrawalDetails =
({
  paymentMethod,
  requestedAt,
  completedAt,
  transactionHash,
  network,
  networkFeeAmount,
  finalAmountCrypto,
  amount,
  fiat,
  isFiatWithdrawal = false,
  logo
}: WithdrawalDetailsProps): ReactElement => {
  const handleExternalLinkClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  const requestedDate = useMemo(() => new Date(requestedAt), [requestedAt])
  const completedDate = useMemo(() => (completedAt ? new Date(completedAt) : null), [completedAt])

  const explorerTxUrl = useMemo(() => {
    if (!transactionHash) return ''
    return formatExplorerUrl(network, 'tx', transactionHash)
  }, [network, transactionHash])

  const conversionRate = useMemo(() => {
    if (!isFiatWithdrawal || !fiat?.finalAmount) return null
    const a = Number(amount)
    const f = Number(fiat.finalAmount)
    if (!Number.isFinite(a) || a === 0 || !Number.isFinite(f)) return null
    return trimZeros((f / a).toFixed(6))
  }, [amount, fiat?.finalAmount, isFiatWithdrawal])

  const tableHeaders = useMemo(() => {
    if (!transactionHash || !completedAt) return null
    return [
      <span key="key">Transaction hash</span>,
      <span key="network">Network</span>,
      <span key="amount">Asset</span>,
      <span key="date">Completed date</span>
    ]
  }, [transactionHash, completedAt])

  const tableItems = useMemo(() => {
    if (!transactionHash || !completedAt || !completedDate) return null

    return [
      [
        <div key="1">
          <ExternalLinkText
            style={externalLinkTextStyle}
            onClick={handleExternalLinkClick}
            target="_blank"
            rel="noreferrer"
            href={explorerTxUrl}
          >
            {shortAddress(transactionHash ?? '', 18)}
            <LinkExternal stroke={RADOM_COLORS.BLACK} width={14} />
          </ExternalLinkText>
        </div>,
        <div key="2" style={rowCenterGap5Style}>
          <div style={rowCenterGap5Style}>
            <img
              src={paymentMethod.hostChain?.logo ?? paymentMethod.logo}
              style={icon18Style}
            />
            <span>
              {paymentMethod.hostChain?.name ?? paymentMethod.name}
            </span>
          </div>
        </div>,
        <div key="3" style={rowCenterGap5Style}>
          <img src={paymentMethod.logo} style={icon18Style} />
          <span>{trimZeros(amount)}</span>
        </div>,
        <TimeText key="4" date={completedDate} />
      ]
    ]
  }, [
    amount,
    completedAt,
    completedDate,
    explorerTxUrl,
    handleExternalLinkClick,
    paymentMethod.hostChain?.logo,
    paymentMethod.hostChain?.name,
    paymentMethod.logo,
    paymentMethod.name,
    transactionHash
  ])

  return (
    <Section>
      <SubTitle style={titleStyle}>
        Withdrawal details
      </SubTitle>

      {
        isFiatWithdrawal && fiat
          ? (
            <SectionContent>
              <SectionItem>
                <SubTitle>Source currency</SubTitle>
                <div style={rowCenterGap3Style}>
                  <img src={paymentMethod.logo} style={icon18Style} />
                  <span>{paymentMethod.ticker}</span>
                </div>
              </SectionItem>

              <SectionItem>
                <SubTitle>Destination currency</SubTitle>
                <div style={rowCenterGap3Style}>
                  <img src={logo} style={icon18Style} />
                  <span>{fiat.currencyOut}</span>
                </div>
              </SectionItem>

              {fiat.finalAmount && (
                <SectionItem>
                  <SubTitle>Final withdrawal amount</SubTitle>
                  <div style={rowCenterGap3Style}>
                    <img src={logo} style={icon18Style} />
                    <span>{trimZeros(fiat.finalAmount)}</span>
                  </div>
                </SectionItem>
              )}

              <SectionItem>
                <SubTitle>Fee</SubTitle>
                <div style={rowCenterGap3Style}>
                  <img src={paymentMethod.logo} style={icon18Style} />
                  <span>{trimZeros(Math.max(fiat.fee, 0).toString())}</span>
                </div>
              </SectionItem>

              <SectionItem>
                <SubTitle>
                  <span>Requested date</span>
                </SubTitle>
                <TimeText key="0" date={requestedDate} />
              </SectionItem>
            </SectionContent>
          )
          : (
            <SectionContent>
              <SectionItem>
                <SubTitle>Asset</SubTitle>
                <div style={rowCenterGap3Style}>
                  <img src={paymentMethod.logo} style={icon18Style} />
                  <span>{paymentMethod.ticker}</span>
                </div>
              </SectionItem>

              {networkFeeAmount && (
                <SectionItem>
                  <SubTitle>
                    <span>Network fee amount</span>
                    <Tooltip message="Fee collected by network.">
                      <HoverInfoIcon width={17} />
                    </Tooltip>
                  </SubTitle>
                  <div style={rowCenterGap3Style}>
                    <img src={paymentMethod.logo} style={icon18Style} />
                    <span>{trimZeros(networkFeeAmount)}</span>
                  </div>
                </SectionItem>
              )}

              {finalAmountCrypto && (
                <SectionItem>
                  <SubTitle>
                    <span>Final amount</span>
                    <Tooltip message="The amount you receive after fee deduction.">
                      <HoverInfoIcon width={17} />
                    </Tooltip>
                  </SubTitle>
                  <div style={rowCenterGap3Style}>
                    <img src={paymentMethod.logo} style={icon18Style} />
                    <span>{trimZeros(finalAmountCrypto)}</span>
                  </div>
                </SectionItem>
              )}

              <SectionItem>
                <SubTitle>
                  <span>Requested date</span>
                </SubTitle>
                <TimeText key="0" date={requestedDate} />
              </SectionItem>
            </SectionContent>
          )
      }

      <SectionBreaker />

      {isFiatWithdrawal && fiat?.finalAmount && (
        <div>
          <SubTitle style={conversionTitleStyle}>Conversion rate</SubTitle>

          <div style={rowCenterGap5Font14Style}>
            <div style={rowCenterGap3Style}>
              <span>1</span>
              <img src={paymentMethod.logo} style={icon18Style} />
              {paymentMethod.ticker}
            </div>

            <div>=</div>

            <div style={rowCenterGap3Style}>
              <span>{conversionRate ?? '-'}</span>
              <img src={logo} style={icon18Style} />
              {fiat.currencyOut}
            </div>
          </div>
        </div>
      )}

      {transactionHash && completedAt && tableHeaders && tableItems && (
        <div>
          <SubTitle style={conversionTitleStyle}>Withdrawal transactions</SubTitle>
          <Table
            disableHoverStyle
            headers={tableHeaders}
            items={tableItems}
            isLoading={false}
          />
        </div>
      )}
    </Section>
  )
}

export default WithdrawalDetails
